import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Pencil, Trash2, Calendar, Tag, Copy, Check, BookOpen, KeyRound, Globe, X, Code, Shield, Monitor } from "lucide-react";
import { clientService, Client, Session, validateOrigin } from "../services/client.service";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../context/ToastContext";
import EditClientModal from "../components/EditClientModal";
import DeleteClientModal from "../components/DeleteClientModal";
import ApiKeyModal from "../components/ApiKeyModal";
import type { ApiKeyType } from "../components/ApiKeyModal";
import "../styles/clients.css";

export default function ClientDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { showToast } = useToast();

    const [client, setClient] = useState<Client | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [sessions, setSessions] = useState<Session[]>([]);
    const [sessionsTotal, setSessionsTotal] = useState(0);
    const [sessionsPage, setSessionsPage] = useState(1);
    const [sessionsLimit] = useState(20);
    const [sessionsLoading, setSessionsLoading] = useState(false);
    const [sessionsLoaded, setSessionsLoaded] = useState(false);

    const [showEdit, setShowEdit] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [copied, setCopied] = useState(false);

    const [regeneratedKey, setRegeneratedKey] = useState<string | null>(null);
    const [regeneratedKeyType, setRegeneratedKeyType] = useState<ApiKeyType>("production");
    const [regeneratingProd, setRegeneratingProd] = useState(false);
    const [generatingDev, setGeneratingDev] = useState(false);
    const [devKeyGenerated, setDevKeyGenerated] = useState(false);

    const [newOrigin, setNewOrigin] = useState("");
    const [originError, setOriginError] = useState("");
    const [savingOrigins, setSavingOrigins] = useState(false);
    const [copiedEmbed, setCopiedEmbed] = useState(false);
    const [activeTab, setActiveTab] = useState<"overview" | "sessions">("overview");
    const [embedTab, setEmbedTab] = useState<"production" | "development">("production");

    const fetchClient = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        setError("");
        try {
            const data = await clientService.getById(id);
            setClient(data);
        } catch (err: any) {
            if (err.response?.status === 404) {
                setError("Client not found.");
            } else {
                setError(err.response?.data?.msg || "Failed to load client.");
            }
        } finally {
            setLoading(false);
        }
    }, [id]);

    const fetchSessions = useCallback(async (page = 1) => {
        if (!id) return;
        setSessionsLoading(true);
        try {
            const res = await clientService.getSessions(id, page, sessionsLimit);
            setSessions(res.data);
            setSessionsTotal(res.total);
            setSessionsPage(res.page);
            setSessionsLoaded(true);
        } catch {
            setSessions([]);
        } finally {
            setSessionsLoading(false);
        }
    }, [id, sessionsLimit]);

    useEffect(() => {
        fetchClient();
    }, [fetchClient]);

    useEffect(() => {
        if (activeTab === "sessions" && client && !sessionsLoaded && !sessionsLoading) {
            fetchSessions(1);
        }
    }, [activeTab, client, sessionsLoaded, sessionsLoading, fetchSessions]);

    const isOwner = user && client && user.id === client.userId;

    const handleUpdated = () => {
        setShowEdit(false);
        fetchClient();
    };

    const handleDeleted = () => {
        setShowDelete(false);
        navigate("/clients", { replace: true });
    };

    const handleRegenerateProdKey = async () => {
        if (!client) return;
        const confirmed = window.confirm(
            "This will invalidate the current production key. Any live widgets using it will stop working. Continue?"
        );
        if (!confirmed) return;
        setRegeneratingProd(true);
        try {
            const { apiKey } = await clientService.regenerateApiKey(client.id);
            setRegeneratedKey(apiKey);
            setRegeneratedKeyType("production");
            showToast("Production API key regenerated successfully.");
        } catch (err: any) {
            showToast(err.response?.data?.msg || "Failed to regenerate production key.", "error");
        } finally {
            setRegeneratingProd(false);
        }
    };

    const handleGenerateDevKey = async () => {
        if (!client) return;
        if (devKeyGenerated) {
            const confirmed = window.confirm(
                "This will invalidate the current development key. Continue?"
            );
            if (!confirmed) return;
        }
        setGeneratingDev(true);
        try {
            const { devApiKey } = await clientService.generateDevKey(client.id);
            setRegeneratedKey(devApiKey);
            setRegeneratedKeyType("development");
            setDevKeyGenerated(true);
            showToast("Development API key generated successfully.");
        } catch (err: any) {
            showToast(err.response?.data?.msg || "Failed to generate development key.", "error");
        } finally {
            setGeneratingDev(false);
        }
    };

    const handleAddOrigin = async () => {
        if (!client || !newOrigin.trim()) return;
        const origin = newOrigin.trim();
        const validationErr = validateOrigin(origin);
        if (validationErr) {
            setOriginError(validationErr);
            return;
        }
        const current = client.allowedOrigins || [];
        if (current.includes(origin)) {
            setOriginError("This origin is already added.");
            return;
        }
        setOriginError("");
        setSavingOrigins(true);
        try {
            await clientService.update(client.id, { allowedOrigins: [...current, origin] });
            setNewOrigin("");
            showToast("Origin added successfully.");
            fetchClient();
        } catch (err: any) {
            setOriginError(err.response?.data?.msg || "Failed to add origin.");
        } finally {
            setSavingOrigins(false);
        }
    };

    const handleRemoveOrigin = async (origin: string) => {
        if (!client) return;
        const current = client.allowedOrigins || [];
        setSavingOrigins(true);
        try {
            await clientService.update(client.id, {
                allowedOrigins: current.filter((o) => o !== origin),
            });
            showToast("Origin removed successfully.");
            fetchClient();
        } catch (err: any) {
            showToast(err.response?.data?.msg || "Failed to remove origin.", "error");
        } finally {
            setSavingOrigins(false);
        }
    };

    const getEmbedCode = (keyPlaceholder: string) => {
        if (!client) return "";
        return `<script>
(function(){
  var s=document.createElement('script');
  s.src='${window.location.origin}/embed.js?clientId=${client.id}&apiKey=${keyPlaceholder}&_t='+Date.now();
  s.async=true;
  document.body.appendChild(s);
})();
</script>`;
    };

    const handleCopyEmbed = () => {
        const placeholder = embedTab === "production" ? "YOUR_PRODUCTION_API_KEY" : "YOUR_DEV_API_KEY";
        navigator.clipboard.writeText(getEmbedCode(placeholder));
        setCopiedEmbed(true);
        setTimeout(() => setCopiedEmbed(false), 2000);
    };

    const formatDate = (iso: string) =>
        new Date(iso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });

    if (loading) {
        return (
            <div className="clients-container">
                <div className="clients-loading">
                    <div className="auth-spinner" />
                </div>
            </div>
        );
    }

    if (error || !client) {
        return (
            <div className="clients-container">
                <div className="clients-header">
                    <Link to="/clients" className="btn-back">
                        <ArrowLeft size={16} />
                        <span>Back to clients</span>
                    </Link>
                </div>
                <div className="clients-empty">
                    <h3>{error || "Client not found"}</h3>
                    <p>The client you're looking for doesn't exist or has been removed.</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="clients-container">
                <div className="clients-header">
                    <Link to="/clients" className="btn-back">
                        <ArrowLeft size={16} />
                        <span>Back to clients</span>
                    </Link>
                </div>

                <div className="client-detail-card">
                    <div className="client-detail-top">
                        <div>
                            <h2>{client.name}</h2>
                            <div className="client-id-row">
                                <code className="client-id-value">{client.id}</code>
                                <button
                                    className="btn-copy-id"
                                    onClick={() => {
                                        navigator.clipboard.writeText(client.id);
                                        setCopied(true);
                                        setTimeout(() => setCopied(false), 2000);
                                    }}
                                >
                                    {copied ? <Check size={13} /> : <Copy size={13} />}
                                    {copied ? "Copied" : "Copy ID"}
                                </button>
                            </div>
                            <div className="client-detail-meta">
                                {client.displayName && (
                                    <span>
                                        Display: <strong>{client.displayName}</strong>
                                    </span>
                                )}
                                {client.layoutColor && (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                        <span
                                            style={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: 3,
                                                background: client.layoutColor,
                                                display: 'inline-block',
                                            }}
                                        />
                                        {client.layoutColor}
                                    </span>
                                )}
                                {client.type && (
                                    <span>
                                        <Tag size={13} />
                                        {client.type}
                                    </span>
                                )}
                                <span>
                                    <Calendar size={13} />
                                    Created {formatDate(client.createdAt)}
                                </span>
                            </div>
                        </div>
                        {isOwner && (
                            <div className="client-detail-actions">
                                <button className="btn-edit" onClick={() => navigate(`/clients/${client.id}/knowledge`)}>
                                    <BookOpen size={14} />
                                    Knowledge Base
                                </button>
                                <button className="btn-edit" onClick={() => setShowEdit(true)}>
                                    <Pencil size={14} />
                                    Edit
                                </button>
                                <button className="btn-delete" onClick={() => setShowDelete(true)}>
                                    <Trash2 size={14} />
                                    Delete
                                </button>
                            </div>
                        )}
                    </div>

                </div>

                <div className="detail-tabs">
                    <button
                        className={`detail-tab ${activeTab === "overview" ? "active" : ""}`}
                        onClick={() => setActiveTab("overview")}
                    >
                        Overview
                    </button>
                    <button
                        className={`detail-tab ${activeTab === "sessions" ? "active" : ""}`}
                        onClick={() => setActiveTab("sessions")}
                    >
                        Sessions {sessionsLoaded ? `(${sessionsTotal})` : ""}
                    </button>
                </div>

                {activeTab === "overview" && (
                    <>
                        {isOwner && (
                            <div className="client-detail-card">
                                <div className="domain-section">
                                    <div className="domain-section-header">
                                        <KeyRound size={18} />
                                        <h3>API Keys</h3>
                                    </div>
                                    <p className="domain-section-desc">
                                        Manage your production and development API keys separately.
                                    </p>

                                    <div className="api-keys-grid">
                                        <div className="api-key-card">
                                            <div className="api-key-card-header">
                                                <Shield size={16} />
                                                <h4>Production Key</h4>
                                            </div>
                                            <p className="api-key-card-desc">
                                                Use this key in production. It will not work from localhost.
                                            </p>
                                            <div className="api-key-card-masked">
                                                <code>pk_••••••••••••••••</code>
                                            </div>
                                            <button
                                                className="btn-edit"
                                                onClick={handleRegenerateProdKey}
                                                disabled={regeneratingProd}
                                            >
                                                <KeyRound size={14} />
                                                {regeneratingProd ? "Regenerating..." : "Regenerate"}
                                            </button>
                                        </div>

                                        <div className="api-key-card">
                                            <div className="api-key-card-header">
                                                <Monitor size={16} />
                                                <h4>Development Key</h4>
                                            </div>
                                            <p className="api-key-card-desc">
                                                Use this key for local development. It only works from localhost.
                                            </p>
                                            {devKeyGenerated ? (
                                                <div className="api-key-card-masked">
                                                    <code>dk_••••••••••••••••</code>
                                                </div>
                                            ) : (
                                                <div className="api-key-card-masked">
                                                    <code className="api-key-not-generated">No development key generated yet</code>
                                                </div>
                                            )}
                                            <button
                                                className="btn-edit"
                                                onClick={handleGenerateDevKey}
                                                disabled={generatingDev}
                                            >
                                                <KeyRound size={14} />
                                                {generatingDev
                                                    ? "Generating..."
                                                    : devKeyGenerated
                                                        ? "Regenerate"
                                                        : "Generate Dev Key"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {isOwner && (
                            <div className="client-detail-card">
                                <div className="domain-section">
                                    <div className="domain-section-header">
                                        <Globe size={18} />
                                        <h3>Allowed Origins</h3>
                                    </div>
                                    <p className="domain-section-desc">
                                        Restrict which domains can use your widget. Leave empty to allow all origins.
                                    </p>

                                    {(client.allowedOrigins || []).length > 0 ? (
                                        <div className="domain-list">
                                            {client.allowedOrigins!.map((origin) => (
                                                <div key={origin} className="domain-item">
                                                    <code>{origin}</code>
                                                    <button
                                                        className="domain-remove-btn"
                                                        onClick={() => handleRemoveOrigin(origin)}
                                                        disabled={savingOrigins}
                                                        title="Remove origin"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="domain-empty">
                                            No origin restrictions — widget is accessible from any domain.
                                        </div>
                                    )}

                                    <div className="domain-add-row">
                                        <input
                                            type="text"
                                            value={newOrigin}
                                            onChange={(e) => { setNewOrigin(e.target.value); setOriginError(""); }}
                                            placeholder="https://example.com"
                                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddOrigin(); } }}
                                        />
                                        <button
                                            className="btn-edit"
                                            onClick={handleAddOrigin}
                                            disabled={savingOrigins || !newOrigin.trim()}
                                        >
                                            Add
                                        </button>
                                    </div>
                                    {originError && <div className="domain-error">{originError}</div>}
                                </div>
                            </div>
                        )}

                        {isOwner && (
                            <div className="client-detail-card">
                                <div className="embed-section">
                                    <div className="domain-section-header">
                                        <Code size={18} />
                                        <h3>Integration Guide</h3>
                                    </div>
                                    <p className="domain-section-desc">
                                        Follow these steps to add the chat widget to your website.
                                    </p>

                                    <div className="guide-steps">
                                        <div className="guide-step">
                                            <div className="guide-step-number">1</div>
                                            <div className="guide-step-content">
                                                <h4>Generate API Keys</h4>
                                                <p>Use the <strong>API Keys</strong> section above to generate your production and/or development keys. Save them — they won't be shown again.</p>
                                            </div>
                                        </div>

                                        <div className="guide-step">
                                            <div className="guide-step-number">2</div>
                                            <div className="guide-step-content">
                                                <h4>Add Allowed Origins</h4>
                                                <p>In the <strong>Allowed Origins</strong> section above, add your website domain (e.g. <code>https://yoursite.com</code>). This restricts who can use your widget.</p>
                                            </div>
                                        </div>

                                        <div className="guide-step">
                                            <div className="guide-step-number">3</div>
                                            <div className="guide-step-content">
                                                <h4>Copy the Embed Snippet</h4>
                                                <p>Paste this code before the closing <code>&lt;/body&gt;</code> tag. Replace the placeholder with your actual API key.</p>

                                                <div className="embed-tabs">
                                                    <button
                                                        className={`embed-tab ${embedTab === "production" ? "active" : ""}`}
                                                        onClick={() => setEmbedTab("production")}
                                                    >
                                                        <Shield size={13} />
                                                        Production
                                                    </button>
                                                    <button
                                                        className={`embed-tab ${embedTab === "development" ? "active" : ""}`}
                                                        onClick={() => setEmbedTab("development")}
                                                    >
                                                        <Monitor size={13} />
                                                        Development
                                                    </button>
                                                </div>

                                                {embedTab === "production" && (
                                                    <div className="embed-tab-hint">
                                                        Use this snippet on your live website. Replace <code>YOUR_PRODUCTION_API_KEY</code> with your production key.
                                                    </div>
                                                )}
                                                {embedTab === "development" && (
                                                    <div className="embed-tab-hint">
                                                        Use this snippet for local testing (localhost only). Replace <code>YOUR_DEV_API_KEY</code> with your development key.
                                                    </div>
                                                )}

                                                <div className="embed-code-block">
                                                    <pre><code>{getEmbedCode(embedTab === "production" ? "YOUR_PRODUCTION_API_KEY" : "YOUR_DEV_API_KEY")}</code></pre>
                                                    <button className="apikey-copy-btn embed-copy-btn" onClick={handleCopyEmbed}>
                                                        {copiedEmbed ? <Check size={14} /> : <Copy size={14} />}
                                                        {copiedEmbed ? "Copied" : "Copy"}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="guide-step">
                                            <div className="guide-step-number">4</div>
                                            <div className="guide-step-content">
                                                <h4>Test It</h4>
                                                <p>Open your website — the chat widget should appear at the bottom-right corner. Click it to start a conversation.</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {activeTab === "sessions" && (
                    <div className="client-detail-card">
                        <div className="sessions-section">
                            <h3>Sessions {sessionsLoaded ? `(${sessionsTotal})` : ""}</h3>
                            {sessionsLoading ? (
                                <div className="sessions-empty">
                                    <div className="auth-spinner" />
                                </div>
                            ) : sessions.length === 0 ? (
                                <div className="sessions-empty">
                                    No sessions yet for this client.
                                </div>
                            ) : (
                                <>
                                    <div className="sessions-list">
                                        {sessions.map((session) => (
                                            <div key={session.id} className="session-item">
                                                <div className="session-item-main">
                                                    <span className="session-item-id">{session.id}</span>
                                                    {session.userAgent && (
                                                        <span className="session-item-ua">{session.userAgent}</span>
                                                    )}
                                                </div>
                                                <span className="session-item-date">
                                                    {formatDate(session.createdAt)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                    {sessionsTotal > sessionsLimit && (
                                        <div className="kb-pagination">
                                            <span className="kb-pagination-info">
                                                Page {sessionsPage} of {Math.ceil(sessionsTotal / sessionsLimit)}
                                            </span>
                                            <div className="kb-pagination-controls">
                                                <button
                                                    className="kb-page-btn"
                                                    disabled={sessionsPage <= 1}
                                                    onClick={() => fetchSessions(sessionsPage - 1)}
                                                >
                                                    Previous
                                                </button>
                                                <button
                                                    className="kb-page-btn"
                                                    disabled={sessionsPage >= Math.ceil(sessionsTotal / sessionsLimit)}
                                                    onClick={() => fetchSessions(sessionsPage + 1)}
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {showEdit && (
                <EditClientModal
                    client={client}
                    onClose={() => setShowEdit(false)}
                    onUpdated={handleUpdated}
                />
            )}
            {showDelete && (
                <DeleteClientModal
                    client={client}
                    onClose={() => setShowDelete(false)}
                    onDeleted={handleDeleted}
                />
            )}
            {regeneratedKey && (
                <ApiKeyModal
                    apiKey={regeneratedKey}
                    keyType={regeneratedKeyType}
                    clientName={client.name}
                    clientId={client.id}
                    allowedOrigins={client.allowedOrigins}
                    onClose={() => setRegeneratedKey(null)}
                />
            )}
        </>
    );
}
